import { DAEMON_URL } from "../config";

async function post<T>(path: string, body: unknown): Promise<{ status: number; data: T }> {
  let res: Response;
  try {
    res = await fetch(`${DAEMON_URL}${path}`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      credentials: "include",
      body: JSON.stringify(body),
    });
  } catch (e) {
    throw new Error(`network error: ${(e as Error).message}`);
  }
  const text = await res.text();
  let data: unknown;
  try { data = text ? JSON.parse(text) : {}; } catch { data = {}; }
  return { status: res.status, data: data as T };
}

export type RegisterResponse = {
  userId: number;
  email: string;
  role: "admin" | "member";
  totpProvisioningUri: string;
  totpSecret: string;
};

export type LoginInitiateResponse = {
  requires2fa?: boolean;
  requirePasswordChange?: boolean;
  userId: number;
};
export type AuthedUser = {
  email: string;
  role: "admin" | "member";
  requirePasswordChange?: boolean;
};

export async function register(email: string, password: string): Promise<RegisterResponse> {
  const r = await post<RegisterResponse | { error: string }>("/auth/register", { email, password });
  if (r.status !== 200) throw new Error((r.data as { error: string }).error || `http ${r.status}`);
  return r.data as RegisterResponse;
}

export async function setup2fa(userId: number, code: string): Promise<AuthedUser> {
  const r = await post<AuthedUser | { error: string }>("/auth/2fa/setup", { userId, code });
  if (r.status !== 200) throw new Error((r.data as { error: string }).error || `http ${r.status}`);
  return r.data as AuthedUser;
}

export type LoginNeedsSetup = { kind: "needs_setup"; userId: number };
export type LoginNeeds2FA = { kind: "needs_2fa"; userId: number };
export type LoginResult = LoginNeedsSetup | LoginNeeds2FA;

export async function login(email: string, password: string): Promise<LoginResult> {
  const r = await post<LoginInitiateResponse | { error: string; userId?: number }>("/auth/login", { email, password });
  if (r.status === 403) {
    const body = r.data as { error?: string; userId?: number };
    if (typeof body.userId === "number") return { kind: "needs_setup", userId: body.userId };
    throw new Error(body.error || `http 403`);
  }
  if (r.status !== 200) {
    const err = r.data as { error: string };
    throw new Error(err.error || `http ${r.status}`);
  }
  const data = r.data as LoginInitiateResponse;
  return { kind: "needs_2fa", userId: data.userId };
}

export type Init2faResponse = {
  userId: number;
  email: string;
  totpProvisioningUri: string;
  totpSecret: string;
};

export async function init2fa(email: string, password: string): Promise<Init2faResponse> {
  const r = await post<Init2faResponse | { error: string }>("/auth/2fa/init", { email, password });
  if (r.status !== 200) throw new Error((r.data as { error: string }).error || `http ${r.status}`);
  return r.data as Init2faResponse;
}

export async function login2fa(userId: number, code: string): Promise<AuthedUser> {
  const r = await post<AuthedUser | { error: string }>("/auth/login/2fa", { userId, code });
  if (r.status !== 200) throw new Error((r.data as { error: string }).error || `http ${r.status}`);
  return r.data as AuthedUser;
}

export async function changePassword(
  currentPassword: string,
  newPassword: string,
): Promise<void> {
  const r = await post<{ ok: true } | { error: string }>("/auth/change-password", {
    currentPassword,
    newPassword,
  });
  if (r.status !== 200) {
    throw new Error((r.data as { error: string }).error || `http ${r.status}`);
  }
}

export async function logout(): Promise<void> {
  await post("/auth/logout", {});
}

export async function needsBootstrap(): Promise<boolean> {
  let res: Response;
  try {
    res = await fetch(`${DAEMON_URL}/auth/needs-bootstrap`, {
      method: "GET",
      headers: { Accept: "application/json" },
      credentials: "include",
    });
  } catch (e) {
    throw new Error(`network error: ${(e as Error).message}`);
  }
  if (!res.ok) throw new Error(`http ${res.status}`);
  const j = (await res.json()) as { needsBootstrap: boolean };
  return j.needsBootstrap;
}
